import React from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Edit2, Trash2 } from 'lucide-react';
import { Modal } from '../common/Modal';
import parse from 'html-react-parser';

interface News {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  updatedAt?: Date;
  author: {
    id: string;
    name: string;
  };
  important?: boolean;
  pinned?: boolean;
}

interface NewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  news: News | null;
  onEdit?: (news: News) => void;
  onDelete?: (news: News) => void;
}

export const NewsModal: React.FC<NewsModalProps> = ({
  isOpen,
  onClose,
  news,
  onEdit,
  onDelete
}) => {
  if (!news) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={news.title}
      size="lg"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <div>
          <p>
            {format(news.createdAt, 'PPP', { locale: de })}
            {news.updatedAt && (
              <span className="ml-2 text-gray-400 dark:text-gray-500">
                (Bearbeitet am {format(news.updatedAt, 'PPP', { locale: de })})
              </span>
            )}
          </p>
          </div>
          <div className="flex items-center space-x-2">
            {news.pinned && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                Angepinnt
              </span>
            )}
            {news.important && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                Wichtig
              </span>
            )}
            {onEdit && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onEdit(news)}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                  title="Bearbeiten"
                >
                  <Edit2 className="h-5 w-5" />
                </button>
                {onDelete && (
                  <button
                    onClick={() => onDelete(news)}
                    className="p-2 rounded-lg text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    title="Löschen"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="prose dark:prose-invert max-w-none">
          {parse(news.content)}
        </div>
      </div>
    </Modal>
  );
};
